/*
(C) Copyright 2015–2022 Potsdam Institute for Climate Impact Research (PIK), authors, and contributors, see AUTHORS file.

This file is part of vodle.

vodle is free software: you can redistribute it and/or modify it under the 
terms of the GNU Affero General Public License as published by the Free 
Software Foundation, either version 3 of the License, or (at your option) 
any later version.

vodle is distributed in the hope that it will be useful, but WITHOUT ANY 
WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR 
A PARTICULAR PURPOSE. See the GNU Affero General Public License for more 
details.

You should have received a copy of the GNU Affero General Public License 
along with vodle. If not, see <https://www.gnu.org/licenses/>. 
*/

export const environment = {
  LEAVE_THIS_AS_THE_FIRST_ENTRY: true,
  // Important: leave the previous line exactly as it is!
  production: true,
  // a deployment serves its own privacy statement and imprint from
  // deploy/site/ (deploy/README.md): set the URLs to "./site/privacy.html"
  // and "./site/impressum.html" then. With a privacy statement the app asks
  // for the consent before it stores a vote; without one it asks nobody.
  imprint_url: "./site/impressum.html",
  privacy_statement_url: "./site/privacy.html",
//  imprint_url: "./site/impressum.html",
//  privacy_statement_url: "./site/privacy.html",
  privacy_statement_headline: "Formal Privacy Policy and Terms of Use",
  logging: {
    logLevels: [
      {
        loggerName: "root",
        logLevel: "ERROR"
      },
    ]  
  },
  show_debug_info: false, // must be false in production!
  // Toggle between Matrix and CouchDB backend
  // Set to true to use Matrix protocol, false to use CouchDB
  useMatrixBackend: true,
  matrix: {
    // In production, Matrix API is served via nginx reverse proxy on the
    // same origin. The browser talks to /_matrix/* which nginx forwards
    // to Synapse internally. The SDK uses this as the base URL and appends
    // /_matrix/client/... paths to it.  "/" (or any path, e.g. "/matrix")
    // means the origin the app is served from and is resolved against it at
    // start; override with the homeserver's own URL (e.g.
    // "https://matrix.vodle.example.org") when it is served elsewhere.
    homeserver_url: "/",
    // The homeserver's server_name — PERMANENT: every user id
    // (@<hash>:<server_name>) and every room alias carries it forever.
    // The deployment scripts (deploy/deploy.sh) take it from here and
    // refuse the placeholder.
    server_name: "148.108.205.92.host.secureserver.net",
    // Enable Matrix E2EE (Olm/Megolm)
    /* Matrix end-to-end encryption. OFF, for two reasons of which the
       second would be enough on its own:
       - vodle gives every (poll, voter) its own Matrix account
         (pollAccountName), and the SDK's crypto store is one per browser
         profile and belongs to ONE account, so a POLL account can never
         have crypto at all. The person's own account still could — nothing
         stops the user room being encrypted — which is why this is a
         switch and not a deletion.
       - It would protect nothing. User data, poll data, options, ratings
         and delegations are all STATE events, which megolm never encrypts,
         and the only room ever created with m.room.encryption is the user
         room, whose payloads are state events too. What does protect the
         contents is vodle's own AES-GCM under the poll password and the
         user password, which is untouched by this.
       Turning it on costs every start a 5.4 MB WebAssembly download — 8.9 s
       on the owner's link — and buys published device keys (#327). */
    enable_e2ee: false,
    // Registration token (Synapse: registration_requires_token). vodle
    // registers a Matrix account per user implicitly; with a token, the
    // homeserver does not have to be open to anyone. The token is part of
    // the app bundle, so it deters drive-by registration bots, no more
    // (#327). Empty: open registration (m.login.dummy).
    registration_token: "DNtwxx8G0qsupRz5OAqPXKN9Tq9tkx8j",
    // Guard bot Matrix user ID — this bot is invited to all poll and voter
    // rooms with admin power (100) for server-side deadline enforcement and
    // lets participants into the closed poll rooms. Empty: derived as
    // "@vodle-guard:" + server_name, which is what deploy/deploy.sh registers.
    guard_bot_user_id: "",
    // How long a joiner waits for the guard bot to answer their knock on a
    // closed poll room (#328) before the join fails: the bot answers within
    // a second when it runs; the wait only ends by this timeout when it
    // does not.
    join_timeout_ms: 60000,
    // How many writes a second this client sends to the homeserver (#327).
    // Publishing a poll of fifty voters over five options is some 400 state
    // events; fired at once they empty the account's token bucket and every
    // one of them then retries against a bucket that is still empty. The
    // client therefore spaces its writes, and a refusal widens the spacing
    // further until the server accepts them again.
    //
    // Keep this at or below the homeserver's rc_message.per_second (the
    // deployment settings recommend 1000, deploy/homeserver.vodle.yaml). A
    // homeserver that rate-limits vodle's account not at all — see the
    // admin API's override_ratelimit in documentation/deployment/MATRIX.md
    // — can set 0 here, which turns the spacing off entirely.
    writes_per_second: 1000,
    // How many writes may go at once, before that rate applies at all. The
    // homeserver allows a burst of its own before its limit bites
    // (rc_message.burst_count, 20000 in the deployment settings), and vodle
    // has no reason to be slower than its server asked for: publishing a
    // poll of fifty voters over five options is some 450 writes, which fits
    // inside that burst and so goes at once. The rate above governs what
    // follows once the burst is spent. Keep this at or below the
    // homeserver's rc_message.burst_count.
    write_burst: 20000,
  },
  data_service: {
    central_db_server_url: "https://sandstorm.pik-potsdam.de/couch/",
    central_db_password: "none",
    allow_other_servers: false,
    hash_n_bytes: 32,
    pid_length: 8,
    pwd_length: 16,
    oid_length: 4,
    vid_length: 8,
    did_length: 8,
    nid_length: 4,
//  if a backdoor for law enforcement into the end-to-end encrypted data is required, uncomment:
//    backdoor_public_key: "ea17226c631a8a78c67626136d91980e82328b72e6b536c7df7e68fbb22c2aa7",
    // Matrix backend: poll membership keys and drafts are written to the
    // user room this long after the last change, coalesced (#330):
    matrix_user_data_delay_ms: 1000,
    // how many user-data keys are written to the user room at once. They do
    // not depend on one another, and a published poll writes about
    // thirty-five of them, which one at a time is a round trip each (#327).
    matrix_user_data_concurrency: 8,
  },
  delegation: {
    // ON since 2026-09-13, at the owner's request, so that the feature can
    // be tested on the deployment. What it costs is written down rather
    // than waved away: documentation/PRIVACY.md §6.3 — the delegation id is
    // part of the (unencrypted) event TYPE of the records at both ends, so
    // the homeserver reads the delegation graph BY VID for free, and
    // `del_incoming.<did>` in the delegate's own user room ties their
    // personal account to that poll. Today that adds nothing the user room
    // does not already say (§3), which is why the two have to be fixed
    // together; a deployment that cares must weigh that before turning
    // this on for real users.
    enabled: true,
    // Which kind of delegation this deployment offers (#285):
    //   "simple"    one delegate at a time, for all of the poll's options
    //   "different" a different delegate for different options
    //   "ranked"    several delegates in the voter's order of preference,
    //               the best-ranked of those who accept carrying the wap
    //   "weighted"  several delegates at once, each carrying a share of the
    //               voter's wap, the rest of it still the voter's own
    // It belongs to the deployment and not to the poll: how one voter's wap
    // may reach another is a rule of the vote itself, and whoever writes a
    // poll would otherwise be picking the rule they expect to win under.
    mode: "weighted",
    // max_weight stays at this deployment's value rather than the
    // contributed branch's: it caps how many people's waps one delegate may
    // end up controlling, which is a policy choice and not a detail of the
    // feature, so raising it is the owner's to make deliberately.
    max_weight: 10,
    max_delegations: 3
  },
  no_more_options_time_fraction: 1/2,
  db_put_retry_delay_ms: 100,
  // The wap an option gets when the voter has not rated it, and the
  // setting a new account starts with (the settings page can change it).
  // A deployment whose polls are more about finding what everyone can
  // live with than about picking favourites may want this higher.
  default_wap: 10,
  // AI assessment of a poll (planning/AI_ASSESSMENT.md): a voter hands the
  // poll's state to an AI of their own choice, through its chat page or, with
  // their own key, its API. Nothing passes through this deployment.
  // `api_enabled` switches off only the API path.
  ai_assessment: {
    enabled: true,
    api_enabled: true,
  },
  default_lang: "en",
  github_url: "https://github.com/pik-gane/vodle",
  // where the app is served: invitation links are built from this
  // ("https://" + server_name + "/#/" for the scripted deployment)
  magic_link_base_url: "https://148.108.205.92.host.secureserver.net/#/",
  support_vodle_url: "http://vodle.it/#support",
  tallying: {
    verify_updates: false
  },
  closing: {
    grace_period_1_ms: 3000,
    grace_period_2_ms: 3000,
    grace_period_3_ms: 3000,
    // Matrix backend (#325): how long a client waits for the guard bot to
    // close the poll on the server before closing it by convention, and
    // how often it looks:
    matrix_closure_timeout_ms: 120000,
    matrix_closure_poll_ms: 2000
  },
  max_len: {
    title: 200,
    name: 100,
    desc: 1000,
    url: 200
  },
  polls: {
    max_duration_days: 31,
    delete_after_days: 31
  },
  // Handing this deployment over to another one, e.g. a CouchDB deployment
  // to its Matrix successor (documentation/deployment/MATRIX.md §6):
  handover: {
    // On the deployment being retired: new polls are no longer started
    // here. The "+" button, a new draft and the start of a draft show a
    // notice with this link instead; the polls that run here continue
    // until they end.
    successor_url: "",     // e.g. "https://matrix.vodle.it/#/"
    // On the successor: where the polls that started before the move live
    // on; shown on the "my polls" page. Empty once the predecessor is gone.
    predecessor_url: "",   // e.g. "https://app.vodle.it/#/"
  },
  hosting_institution: {
    name: null,
    url: null
  },
  // Important: leave the next line exactly as it is!
  LEAVE_THIS_AS_THE_LAST_ENTRY: true
};
